import {
    Autocomplete,
    Box,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import LanguageIcon from '@mui/icons-material/Language';
import { CUSTOM_FIELDS_TYPES } from '../../../helpers/constants';

const CustomFieldsEditor = ({ values, setFieldValue }) => {
    const { t } = useTranslation();
    const { customFields } = values;
    const customFieldsTypes = [
        { id: CUSTOM_FIELDS_TYPES.SINGLE, name: t('tickettypes.customFieldsTypes.singleLine') },
        { id: CUSTOM_FIELDS_TYPES.MULTI, name: t('tickettypes.customFieldsTypes.multiLine') },
        { id: CUSTOM_FIELDS_TYPES.DROPDOWN, name: t('tickettypes.customFieldsTypes.dropdown') },
    ];
    const { showModal } = useModals();
    const setField = useCallback(
        (newValue) => {
            setFieldValue('customFields', newValue);
        },
        [setFieldValue],
    );

    return (
        <Box
            key={'customFieldsEditorBox'}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                mb: 3,
                border: '1px solid #ccc',
                borderRadius: 2,
                p: 2,
            }}
        >
            {customFields.map(({ id, type, name, description, required, showInCatering }, idx) => (
                <React.Fragment key={id}>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="subtitle1">{`${t('tickettypes.columns.customFields')} (${idx + 1}.)`}</Typography>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 7 }}>
                            <FormControl fullWidth>
                                <InputLabel id="type">
                                    {t('tickettypes.customFieldsAttributes.type')}
                                </InputLabel>
                                <Select
                                    sx={{ textAlign: 'left' }}
                                    labelId="type"
                                    fullWidth
                                    label={t('tickettypes.customFieldsAttributes.type')}
                                    value={type}
                                    required
                                    onChange={(ev) => {
                                        const isDropdown =
                                            ev.target.value === CUSTOM_FIELDS_TYPES.DROPDOWN;
                                        setField(
                                            customFields.map((line) =>
                                                line.id === id
                                                    ? {
                                                          ...line,
                                                          type: ev.target.value,
                                                          description: isDropdown
                                                              ? ''
                                                              : line.description,
                                                          description_i18n: isDropdown
                                                              ? ''
                                                              : line.description_i18n,
                                                      }
                                                    : line,
                                            ),
                                        );
                                    }}
                                >
                                    {customFieldsTypes.map((line) => (
                                        <MenuItem value={line.id} key={line.id}>
                                            {line.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 2 }} sx={{ textAlign: 'left' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={required}
                                        onChange={(ev) => {
                                            setField(
                                                customFields.map((line) =>
                                                    line.id === id
                                                        ? { ...line, required: ev.target.checked }
                                                        : line,
                                                ),
                                            );
                                        }}
                                    />
                                }
                                label={t('tickettypes.customFieldsAttributes.required')}
                                labelPlacement="end"
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 12, sm: 3 }} sx={{ textAlign: 'left' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!showInCatering}
                                        onChange={(ev) => {
                                            setField(
                                                customFields.map((line) =>
                                                    line.id === id
                                                        ? {
                                                              ...line,
                                                              showInCatering: ev.target.checked,
                                                          }
                                                        : line,
                                                ),
                                            );
                                        }}
                                    />
                                }
                                label={t('tickettypes.customFieldsAttributes.showInCatering')}
                                labelPlacement="end"
                            />
                        </Grid2>
                        <Grid2 size={{ xs: 10, sm: 11 }}>
                            <TextField
                                fullWidth
                                type="text"
                                autoComplete="off"
                                value={name}
                                label={t('tickettypes.customFieldsAttributes.name')}
                                required
                                onChange={(ev) => {
                                    setField(
                                        customFields.map((line) =>
                                            line.id === id
                                                ? { ...line, name: ev.target.value }
                                                : line,
                                        ),
                                    );
                                }}
                            />
                        </Grid2>
                        <Grid2
                            size={{ xs: 2, sm: 1 }}
                            sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                        >
                            <Tooltip title={t('common.translations')}>
                                <IconButton
                                    onClick={() => {
                                        showModal(MODAL_TYPE.TRANSLATIONS, {
                                            translations: values.customFields[idx].name_i18n,
                                            setTranslations: (name_i18n) =>
                                                setFieldValue(
                                                    'customFields',
                                                    customFields.map((line) =>
                                                        line.id === id
                                                            ? { ...line, name_i18n }
                                                            : line,
                                                    ),
                                                ),
                                            item: 'tickettypes',
                                            column: 'name',
                                            intermediateColumn: 'customFieldsAttributes',
                                            venueId: values.venueId,
                                            originalString: name,
                                        });
                                    }}
                                    disabled={!values.venueId}
                                    size="small"
                                    edge="end"
                                >
                                    <LanguageIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                        <Grid2 size={{ xs: 10, sm: 11 }}>
                            {type === CUSTOM_FIELDS_TYPES.DROPDOWN ? (
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={description}
                                    onChange={(_, newValue) => {
                                        setField(
                                            customFields.map((line) =>
                                                line.id === id
                                                    ? { ...line, description: newValue }
                                                    : line,
                                            ),
                                        );
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={t(
                                                'tickettypes.customFieldsAttributes.description',
                                            )}
                                            placeholder={t(
                                                'common.freesoloAutocompletePlaceholder',
                                            )}
                                            fullWidth
                                            style={{ minWidth: '200px' }}
                                        />
                                    )}
                                    getOptionLabel={(option) => {
                                        return `${option}`;
                                    }}
                                />
                            ) : (
                                <TextField
                                    fullWidth
                                    type="text"
                                    autoComplete="off"
                                    value={description}
                                    multiline={type === CUSTOM_FIELDS_TYPES.MULTI}
                                    label={t('tickettypes.customFieldsAttributes.description')}
                                    required
                                    onChange={(ev) => {
                                        setField(
                                            customFields.map((line) =>
                                                line.id === id
                                                    ? { ...line, description: ev.target.value }
                                                    : line,
                                            ),
                                        );
                                    }}
                                />
                            )}
                        </Grid2>
                        <Grid2
                            size={{ xs: 2, sm: 1 }}
                            sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}
                        >
                            <Tooltip title={t('common.translations')}>
                                <IconButton
                                    onClick={() => {
                                        showModal(MODAL_TYPE.TRANSLATIONS, {
                                            translations: values.customFields[idx].description_i18n,
                                            setTranslations: (description_i18n) =>
                                                setFieldValue(
                                                    'customFields',
                                                    customFields.map((line) =>
                                                        line.id === id
                                                            ? { ...line, description_i18n }
                                                            : line,
                                                    ),
                                                ),
                                            item: 'tickettypes',
                                            column: 'description',
                                            intermediateColumn: 'customFieldsAttributes',
                                            venueId: values.venueId,
                                            originalString: description,
                                            // optional fields (type and originalData)
                                            type: values.customFields[idx].type,
                                            originalData: values.customFields[idx].description,
                                        });
                                    }}
                                    disabled={!values.venueId}
                                    size="small"
                                    edge="end"
                                >
                                    <LanguageIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Grid2>
                        <Grid2 size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                            <IconButton
                                onClick={() => {
                                    setField(customFields.filter((e) => e.id !== id));
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>
                        </Grid2>
                    </Grid2>
                    <Divider sx={{ m: 1 }} />
                </React.Fragment>
            ))}
            <Grid2 size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                <IconButton
                    onClick={() => {
                        setField([
                            ...customFields,
                            {
                                id: `${Math.random()}`.substring(2),
                                type: customFieldsTypes[0].id,
                                name: '',
                                name_i18n: {},
                                description: '',
                                description_i18n: {},
                                required: false,
                                showInCatering: false,
                            },
                        ]);
                    }}
                >
                    <Add />
                </IconButton>
            </Grid2>
        </Box>
    );
};

export default CustomFieldsEditor;
